/**
 * Цикл кадров боя: фиксированный шаг 30 Гц с накоплением времени, поле рисуется каждый кадр между тиками
 * (интерполяция `alpha`), при подвисании — не больше 5 тиков за кадр. На паузе (свёрнуто, модальное окно) тики стоят,
 * накопленное время сбрасывается — после паузы бой не «догоняет». `pauseAt` — остановить бой на тике N
 * (`?pause=N`, скриншоты и e2e C1).
 *
 * Источник снимков — геттер: когда забежка начинается заново (новый поток), цикл берёт его `current()`.
 */
import type { BattleSnapshot } from "@newfungame/contracts";
import { TICK_MS } from "@newfungame/contracts";

export interface SnapshotSource {
  current(): BattleSnapshot;
  step(): BattleSnapshot;
}

export interface FrameLoopOptions {
  source: () => SnapshotSource;
  /** Можно делать тики (не пауза). */
  isRunning: () => boolean;
  pauseAt?: number;
  /** Ускорение времени боя (x2 у VIP, `?speed=N` в e2e): `dt` умножается, лимит тиков за кадр прежний. */
  speed?: () => number;
  /** После тиков кадра (или при смене источника) — новый снимок для HUD и отладки. */
  onTick(s: BattleSnapshot): void;
  /** Каждый кадр: нарисовать поле. `alpha` ∈ [0, 1] — доля пути к следующему тику. */
  onFrame(s: BattleSnapshot, alpha: number): void;
}

export interface FrameLoop {
  /** Один кадр в момент `now` (мс, как у `requestAnimationFrame`). Для тестов — без rAF. */
  frame(now: number): void;
  snapshot(): BattleSnapshot;
  start(): void;
  stop(): void;
}

export const MAX_TICKS_PER_FRAME = 5;
/** TICK_MS = 33,(3) мс: без допуска ошибка округления теряет тик (99,99… < 3 · 33,33…). */
const EPS = 1e-6;

export function createFrameLoop(opts: FrameLoopOptions): FrameLoop {
  const pauseAt = opts.pauseAt ?? Infinity;
  const speed = opts.speed ?? (() => 1);
  let src = opts.source();
  let snap = src.current();
  while (pauseAt !== Infinity && snap.frame < pauseAt) snap = src.step();
  let acc = 0;
  let last: number | null = null;
  let alpha = 0;
  let handle: number | null = null;
  opts.onTick(snap);

  const frame = (now: number) => {
    const dt = last === null ? 0 : (now - last) * speed();
    last = now;
    const next = opts.source();
    if (next !== src) {
      src = next;
      snap = src.current();
      acc = 0;
      opts.onTick(snap);
    }
    if (!opts.isRunning()) {
      acc = 0;
    } else {
      acc = Math.min(acc + dt, TICK_MS * MAX_TICKS_PER_FRAME);
      let stepped = false;
      while (acc >= TICK_MS - EPS && snap.frame < pauseAt) {
        acc -= TICK_MS;
        snap = src.step();
        stepped = true;
      }
      alpha = snap.frame < pauseAt ? Math.max(0, acc) / TICK_MS : 1;
      if (stepped) opts.onTick(snap);
    }
    opts.onFrame(snap, alpha);
  };

  const tick = (now: number) => {
    frame(now);
    handle = requestAnimationFrame(tick);
  };

  return {
    frame,
    snapshot: () => snap,
    start() {
      if (handle === null) handle = requestAnimationFrame(tick);
    },
    stop() {
      if (handle !== null) cancelAnimationFrame(handle);
      handle = null;
      last = null;
    },
  };
}
